import { SERVER_URL } from "../config";
import { BillingInfo, IPriceResponse } from "../types/BillingTypes";
import { DefaultResponse } from "../types/UtilTypes";
import { localStorageGetItem } from "../utils/LocalStorage";

interface ICreateStripeCustomer {
  name: string;

  billing_address: {
    street: string;
    state_region: string;
    city: string;
    country: string;
    zip_code: string;
  };

  shipping_address: {
    street: string;
    state_region: string;
    city: string;
    country: string;
    zip_code: string;
  };

  vatId?: string;
  companyName?: string;
}

interface IAddStripePaymentMethod {
  paymentMethodId: string;
}

interface IStartSubscription {
  externalUsers: number;
  isUpdate: boolean;
  admins: number;
  tier: number;
  hasFee?: boolean;
}

interface IGetUpsell {
  upsellUid: UpsellUid;
}

export enum UpsellUid {
  ContentCreation = "ContentCreation",
  EmailPlayersListSwell = "EmailPlayersListSwell",
  AutomationMagic = "AutomationMagic",
  SubscriptionBiz = "SubscriptionBiz",
}

export async function getBillingInfo() {
  let userUid = localStorageGetItem("USER_UID");
  if (!userUid) {
    userUid = localStorage.getItem("bid");
  }

  const request = await fetch(`${SERVER_URL}/billing/${userUid}`, {
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
  });

  const billingInfo = (await request.json()).data as BillingInfo | null;

  return billingInfo;
}

export async function getOldInvoices() {
  throw new Error("Not implemented");
  let accessToken = localStorage.getItem("accessToken");
  if (!accessToken) {
    accessToken = localStorage.getItem("bid");
  }

  const request = await fetch(`${SERVER_URL}/billing/invoice/user`, {
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      Authorization: "Bearer " + accessToken,
    },
  });

  const billingInfo = (await request.json()) as {
    _id: string;
    invoiceNumber: number;
    invoiceDate: string;
  }[];

  return billingInfo;
}

export async function downloadInvoice(invoiceId: string) {
  throw new Error("Not implemented");
  let accessToken = localStorage.getItem("accessToken");
  if (!accessToken) {
    accessToken = localStorage.getItem("bid");
  }

  const request = await fetch(`${SERVER_URL}/billing/invoice/${invoiceId}`, {
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      Authorization: "Bearer " + accessToken,
    },
  });

  const pdf = request.text();

  return pdf;
}

export async function createStripeCustomer(data: ICreateStripeCustomer) {
  try {
    let userUid = localStorageGetItem("USER_UID");

    if (!userUid) {
      userUid = localStorage.getItem("bid");
    }

    const response = await fetch(`${SERVER_URL}/billing/customer`, {
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        // Authorization: "Bearer " + accessToken,
      },
      method: "post",
      body: JSON.stringify({ ...data, userUid }),
    });

    const responseData = (await response.json()).data;

    return {
      success: true,
      message: null,
      id: responseData.id,
    };
  } catch (err) {
    return {
      success: false,
      message: err.message,
      id: null,
    };
  }
}

export async function updateStripeCustomer(data: ICreateStripeCustomer) {
  try {
    let userUid = localStorageGetItem("USER_UID");
    if (!userUid) {
      userUid = localStorage.getItem("bid");
    }
    const response = await fetch(`${SERVER_URL}/billing/customer`, {
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      method: "put",
      body: JSON.stringify({ ...data, userUid }),
    });

    const responseData = (await response.json()).data;

    return {
      success: true,
      message: null,
      id: responseData.id,
    };
  } catch (err) {
    return {
      success: false,
      message: err.message,
      id: null,
    };
  }
}

export async function updateBilling(data: Partial<ICreateStripeCustomer>) {
  try {
    let userUid = localStorageGetItem("USER_UID");

    const response = await fetch(`${SERVER_URL}/billing/customer/${userUid}`, {
      headers: {
        "Content-Type": "application/json",
      },
      method: "PATCH",
      body: JSON.stringify({ ...data }),
    });

    const responseData = (await response.json()).data;

    return {
      success: true,
      message: null,
      id: responseData.id,
    };
  } catch (err) {
    return {
      success: false,
      message: err.message,
      id: null,
    };
  }
}

export async function addStripePaymentMethod(data: IAddStripePaymentMethod) {
  try {
    let userUid = localStorageGetItem("USER_UID");
    if (!userUid) {
      userUid = localStorage.getItem("bid");
    }
    const response = await fetch(
      `${SERVER_URL}/billing/paymentMethod/${userUid}`,
      {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          // Authorization: "Bearer " + accessToken,
        },
        method: "post",
        body: JSON.stringify(data),
      }
    );

    const responseData = (await response.json()).data;

    if (responseData.error) {
      throw new Error(responseData.error.message);
    }

    return {
      success: true,
      message: null,
      id: responseData.id,
    };
  } catch (err) {
    return {
      success: false,
      message: err.message,
      id: null,
    };
  }
}

function handleRequiresPaymentMethod({
  subscription,
  paymentMethodId,
  priceId,
}: {
  subscription: any;
  paymentMethodId: any;
  priceId: any;
}) {
  if (subscription.status === "active") {
    // subscription is active, no customer actions required.
    return { subscription, priceId, paymentMethodId };
  } else if (
    subscription.latest_invoice.payment_intent.status ===
    "requires_payment_method"
  ) {
    // Using localStorage to manage the state of the retry here,
    // feel free to replace with what you prefer.
    // Store the latest invoice ID and status.
    localStorage.setItem("latestInvoiceId", subscription.latest_invoice.id);
    localStorage.setItem(
      "latestInvoicePaymentIntentStatus",
      subscription.latest_invoice.payment_intent.status
    );
    throw { error: { message: "Your card was declined." } };
  } else {
    return { subscription, priceId, paymentMethodId };
  }
}

export async function startSubscription(data: IStartSubscription) {
  const { externalUsers, isUpdate, admins, tier, hasFee } = data;

  let userUid = localStorageGetItem("USER_UID");

  if (!userUid) {
    userUid = localStorage.getItem("bid");
  }

  return (
    fetch(`${SERVER_URL}/billing/subscription`, {
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        // Authorization: "Bearer " + accessToken,
      },
      method: isUpdate ? "put" : "post",
      body: JSON.stringify({
        admins,
        externalUsers,
        newAdmins: isUpdate ? admins : undefined,
        newExternalUsers: isUpdate ? externalUsers : undefined,
        newTier: isUpdate ? tier : undefined,
        userUid,
        tier,
        hasFee,
      }),
    })
      .then((response) => {
        return response.json();
      })
      // If the card is declined, display an error to the user.
      .then((result) => {
        if (result.error || result.code) {
          // The card had an error when trying to attach it to a customer.
          throw result;
        }
        return result.data;
      })
      // Normalize the result to contain the object returned by Stripe.
      // Add the additional details we need.
      .then((result) => {
        return {
          subscription: result,
        };
      })
      // If attaching this card to a Customer object succeeds,
      // but attempts to charge the customer fail, you
      // get a requires_payment_method error.
      //@ts-ignore
      .then(handleRequiresPaymentMethod)
      // No more actions required. Provision your service for the user.
      .then(() => console.log("done"))
      .catch((error) => {
        // An error has happened. Display the failure to the user here.
        // We utilize the HTML element we created.
        console.log(error);
        throw error;
      })
  );
}

export async function getStripeLink() {
  let userUid = localStorageGetItem("USER_UID");

  const request = await fetch(
    `${SERVER_URL}/billing/create-customer-portal-session/${userUid}`,
    {
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        // Authorization: "Bearer " + accessToken,
      },
    }
  );

  const data = await request.json();

  return data.url as string;
}

export async function cancelSubscription(userUid: string) {
  const response = await fetch(
    `${SERVER_URL}/billing/subscription/${userUid}`,
    {
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      method: "DELETE",
    }
  );

  const responseData =
    (await response.json()) as DefaultResponse<IPriceResponse>;
  if (responseData.status === "ok") {
    return responseData.data;
  } else {
    throw new Error(responseData.message);
  }
}

export async function getBasePrice() {
  let userUid = localStorageGetItem("USER_UID");
  if (!userUid) {
    userUid = localStorage.getItem("bid");
  }
  const response = await fetch(`${SERVER_URL}/billing/price/${userUid}`, {
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    method: "get",
  });

  const responseData =
    (await response.json()) as DefaultResponse<IPriceResponse>;
  if (responseData.status === "ok") {
    return responseData.data;
  } else {
    throw new Error(responseData.message);
  }
}

export async function getUpsellsPrice() {
  const response = await fetch(`${SERVER_URL}/billing/upsells`, {
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    method: "get",
  });

  const responseData = await response.json();
  if (response.status === 200) {
    return responseData.data;
  } else {
    throw new Error(responseData.message);
  }
}

export async function buyUpsell(data: IGetUpsell) {
  const { upsellUid } = data;

  let userUid = localStorageGetItem("USER_UID");

  if (!userUid) {
    userUid = localStorage.getItem("bid");
  }

  const response = await fetch(`${SERVER_URL}/billing/upsell`, {
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    method: "post",
    body: JSON.stringify({
      upsellUid,
      userUid,
    }),
  });

  const responseData = await response.json();
  if (response.status === 200) {
    return responseData;
  } else {
    throw new Error(responseData.message);
  }
}

export async function finishUpsells() {
  let userUid = localStorageGetItem("USER_UID");

  if (!userUid) {
    userUid = localStorage.getItem("bid");
  }

  const response = await fetch(`${SERVER_URL}/billing/upsellDone/${userUid}`, {
    method: "get",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
  });

  return response.json();
}

async function sendTapfilliate({ referralCode }: { referralCode: string }) {
  try {
    const response: Response = await fetch(
      `${SERVER_URL}/billing/tapfiliate/click`,
      {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        method: "post",
        body: JSON.stringify({
          referralCode,
          landingPage: window.location.href,
        }),
      }
    );

    const responseData = await response.json();
    return responseData;
  } catch (error) {
    console.log(error);
  }
}
export const BillingAPI = {
  sendTapfilliate,
};
